import React, { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  FiAlertCircle,
  FiCheckCircle,
  FiChevronRight,
  FiHome,
  FiMail,
  FiPhone,
  FiRefreshCw,
  FiSearch,
  FiUsers,
  FiXCircle,
} from 'react-icons/fi';
import PageLayout from '../components/layout/PageLayout';
import { getAllHosts } from '../store/thunks/HostActions';
import { deleteHostReset } from '../store/slices/HostSlices';

const numberFormatter = new Intl.NumberFormat('en-US');

const HOST_FILTERS = [
  { id: 'all', label: 'All Hosts' },
  { id: 'active', label: 'Active' },
  { id: 'inactive', label: 'Inactive' },
  { id: 'no-properties', label: 'No Properties' },
];

const normalizeText = (value = '') =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9@.\-\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const splitTerms = (value) => normalizeText(value).split(' ').filter(Boolean);

const joinClasses = (...classes) => classes.filter(Boolean).join(' ');

const getHostName = (host) => {
  const fullName = `${host?.firstName || ''} ${host?.lastName || ''}`.trim();
  return fullName || host?.companyName || host?.email || 'Unnamed host';
};

const getInitials = (host) => {
  const initials = getHostName(host)
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

  return initials || 'H';
};

const isActiveHost = (host) => Boolean(host?.isVerified && host?.isOnBoarded);

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

function SummaryCard({ label, value, detail, icon: Icon, tone = 'zinc' }) {
  const toneClasses = {
    zinc: 'bg-white border-zinc-200 text-zinc-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    sky: 'bg-sky-50 border-sky-200 text-sky-900',
  };

  const iconClasses = {
    zinc: 'bg-zinc-100 text-zinc-600',
    emerald: 'bg-emerald-100 text-emerald-700',
    amber: 'bg-amber-100 text-amber-700',
    sky: 'bg-sky-100 text-sky-700',
  };

  return (
    <div className={joinClasses('rounded-2xl border px-4 py-3', toneClasses[tone] || toneClasses.zinc)}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] opacity-70">{label}</p>
          <p className="mt-2 text-2xl font-semibold tracking-tight">{value}</p>
          {detail ? <p className="mt-1 text-xs opacity-70">{detail}</p> : null}
        </div>
        {Icon ? (
          <div className={joinClasses('flex h-10 w-10 items-center justify-center rounded-xl', iconClasses[tone] || iconClasses.zinc)}>
            <Icon className="h-5 w-5" />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function StatusBadge({ active }) {
  return (
    <span
      className={joinClasses(
        'inline-flex items-center rounded-full px-2 py-1 text-[11px] font-semibold uppercase tracking-wide ring-1',
        active
          ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
          : 'bg-amber-50 text-amber-700 ring-amber-200'
      )}
    >
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

function SecondaryBadge({ label, tone = 'zinc' }) {
  const classes = {
    zinc: 'bg-zinc-100 text-zinc-600 ring-zinc-200',
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    amber: 'bg-amber-50 text-amber-700 ring-amber-200',
    sky: 'bg-sky-50 text-sky-700 ring-sky-200',
  };

  return (
    <span className={joinClasses('inline-flex items-center rounded-full px-2 py-1 text-[11px] font-medium ring-1', classes[tone] || classes.zinc)}>
      {label}
    </span>
  );
}

function InlineBanner({ tone = 'error', children, onClose }) {
  const styles = {
    error: 'border-red-200 bg-red-50 text-red-700',
    success: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  };

  return (
    <div className={joinClasses('mb-4 flex items-start justify-between gap-3 rounded-2xl border px-4 py-3 text-sm', styles[tone] || styles.error)}>
      <div className="flex items-start gap-2">
        {tone === 'success' ? <FiCheckCircle className="mt-0.5 h-4 w-4" /> : <FiAlertCircle className="mt-0.5 h-4 w-4" />}
        <div>{children}</div>
      </div>
      {onClose ? (
        <button type="button" onClick={onClose} className="opacity-70 transition hover:opacity-100">
          <FiXCircle className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}

export default function HostListScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, hostList, error } = useSelector((state) => state.allHosts);
  const {
    message: deleteMessage,
    success: deleteSuccess,
    error: deleteError,
  } = useSelector((state) => state.deleteHost);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [refreshing, setRefreshing] = useState(false);

  const deferredSearch = useDeferredValue(search);
  const searchTerms = useMemo(() => splitTerms(deferredSearch), [deferredSearch]);

  const loadHosts = async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    try {
      await dispatch(getAllHosts({ limit: 1000, status: 'all' })).unwrap();
    } catch (_error) {
      // Slice state drives the error banner for this screen.
    } finally {
      if (silent) setRefreshing(false);
    }
  };

  useEffect(() => {
    loadHosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!deleteSuccess && !deleteError) return undefined;
    const timeoutId = setTimeout(() => {
      dispatch(deleteHostReset());
    }, 3000);

    return () => clearTimeout(timeoutId);
  }, [deleteError, deleteSuccess, dispatch]);

  const hosts = useMemo(() => {
    if (!Array.isArray(hostList)) return [];

    return hostList.map((host) => {
      const active = isActiveHost(host);
      const propertyCount = Number(host?.propertyCount || 0);
      const company = host?.companyName?.trim() || '';
      const phone = host?.phoneNumber?.trim() || '';

      return {
        ...host,
        active,
        propertyCount,
        company,
        phone,
        displayName: getHostName(host),
        searchText: normalizeText([
          getHostName(host),
          host?.email,
          phone,
          company,
        ].filter(Boolean).join(' ')),
      };
    });
  }, [hostList]);

  const filterCounts = useMemo(() => {
    return hosts.reduce(
      (acc, host) => {
        acc.all += 1;
        if (host.active) acc.active += 1;
        if (!host.active) acc.inactive += 1;
        if (!host.propertyCount) acc['no-properties'] += 1;
        return acc;
      },
      {
        all: 0,
        active: 0,
        inactive: 0,
        'no-properties': 0,
      }
    );
  }, [hosts]);

  const filteredHosts = useMemo(() => {
    const nextHosts = hosts.filter((host) => {
      if (filterStatus === 'active' && !host.active) return false;
      if (filterStatus === 'inactive' && host.active) return false;
      if (filterStatus === 'no-properties' && host.propertyCount > 0) return false;
      if (searchTerms.length && !searchTerms.every((term) => host.searchText.includes(term))) return false;
      return true;
    });

    return nextHosts.sort((left, right) => {
      if (right.active !== left.active) return Number(right.active) - Number(left.active);
      if (right.propertyCount !== left.propertyCount) return right.propertyCount - left.propertyCount;
      return left.displayName.localeCompare(right.displayName);
    });
  }, [filterStatus, hosts, searchTerms]);

  const totalProperties = useMemo(
    () => hosts.reduce((sum, host) => sum + host.propertyCount, 0),
    [hosts]
  );

  const verifiedCount = useMemo(
    () => hosts.filter((host) => host.isVerified).length,
    [hosts]
  );

  const displayedProperties = useMemo(
    () => filteredHosts.reduce((sum, host) => sum + host.propertyCount, 0),
    [filteredHosts]
  );

  return (
    <PageLayout
      title="Property Hosts"
      subtitle="Manage host accounts and property access at a glance."
      showSearch={false}
      fullWidth
      actions={(
        <button
          type="button"
          onClick={() => loadHosts({ silent: true })}
          disabled={loading || refreshing}
          className="inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <FiRefreshCw className={joinClasses('h-4 w-4', refreshing ? 'animate-spin' : '')} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      )}
    >
      {deleteSuccess && deleteMessage ? (
        <InlineBanner tone="success" onClose={() => dispatch(deleteHostReset())}>
          {deleteMessage}
        </InlineBanner>
      ) : null}

      {deleteError ? (
        <InlineBanner tone="error" onClose={() => dispatch(deleteHostReset())}>
          {deleteError}
        </InlineBanner>
      ) : null}

      {error ? (
        <InlineBanner tone="error">
          Failed to load property hosts. Refresh and try again.
        </InlineBanner>
      ) : null}

      <div className="space-y-6">
        <section className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <SummaryCard
            label="Total Hosts"
            value={numberFormatter.format(filterCounts.all)}
            detail={`${numberFormatter.format(totalProperties)} linked properties`}
            icon={FiUsers}
            tone="zinc"
          />
          <SummaryCard
            label="Active"
            value={numberFormatter.format(filterCounts.active)}
            detail="Verified and onboarded"
            icon={FiCheckCircle}
            tone="emerald"
          />
          <SummaryCard
            label="Inactive"
            value={numberFormatter.format(filterCounts.inactive)}
            detail="Needs verification or onboarding"
            icon={FiAlertCircle}
            tone="amber"
          />
          <SummaryCard
            label="Visible Properties"
            value={numberFormatter.format(displayedProperties)}
            detail={`${numberFormatter.format(verifiedCount)} verified hosts`}
            icon={FiHome}
            tone="sky"
          />
        </section>

        <section className="rounded-[28px] border border-zinc-200 bg-white p-4 shadow-sm sm:p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-indigo-600">
                Host Workspace
              </p>
              <h2 className="mt-1 text-lg font-semibold text-zinc-900">
                Unified view of active and inactive hosts
              </h2>
              <p className="mt-1 text-sm text-zinc-500">
                Search across host identity fields, then use status filters to isolate onboarding and property coverage gaps.
              </p>
            </div>

            <div className="relative w-full lg:max-w-md">
              <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search name, email, phone, or company"
                className="w-full rounded-2xl border border-zinc-200 bg-zinc-50 py-3 pl-10 pr-4 text-sm text-zinc-900 outline-none transition focus:border-indigo-300 focus:bg-white focus:ring-2 focus:ring-indigo-100"
              />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {HOST_FILTERS.map((filter) => {
              const active = filter.id === filterStatus;
              return (
                <button
                  key={filter.id}
                  type="button"
                  onClick={() => setFilterStatus(filter.id)}
                  className={joinClasses(
                    'inline-flex items-center gap-2 rounded-full border px-3 py-2 text-sm font-medium transition',
                    active
                      ? 'border-zinc-900 bg-zinc-900 text-white shadow-sm'
                      : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300 hover:text-zinc-900'
                  )}
                >
                  <span>{filter.label}</span>
                  <span className={joinClasses('rounded-full px-2 py-0.5 text-[11px]', active ? 'bg-white/15 text-white' : 'bg-zinc-100 text-zinc-500')}>
                    {numberFormatter.format(filterCounts[filter.id] || 0)}
                  </span>
                </button>
              );
            })}

            {(search || filterStatus !== 'all') ? (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setFilterStatus('all');
                }}
                className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-600 transition hover:bg-zinc-100 hover:text-zinc-900"
              >
                <FiXCircle className="h-4 w-4" />
                Clear
              </button>
            ) : null}
          </div>

          <div className="mt-3 text-sm text-zinc-500">
            Showing <span className="font-semibold text-zinc-900">{numberFormatter.format(filteredHosts.length)}</span> hosts
            {filteredHosts.length !== filterCounts.all ? (
              <> across <span className="font-semibold text-zinc-900">{numberFormatter.format(displayedProperties)}</span> visible properties</>
            ) : null}
          </div>
        </section>

        <section className="overflow-hidden rounded-[28px] border border-zinc-200 bg-white shadow-sm">
          {loading ? (
            <div className="space-y-3 p-5">
              {[0, 1, 2, 3].map((row) => (
                <div key={row} className="animate-pulse rounded-2xl border border-zinc-200 p-4">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-2xl bg-zinc-200" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-48 rounded bg-zinc-200" />
                      <div className="h-3 w-72 rounded bg-zinc-100" />
                    </div>
                    <div className="h-8 w-24 rounded-full bg-zinc-100" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredHosts.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-zinc-100 text-zinc-400">
                <FiUsers className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-zinc-900">No hosts match the current view</h3>
                <p className="mt-1 text-sm text-zinc-500">
                  Try a different search or clear the filters to return to the full workspace.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setFilterStatus('all');
                }}
                className="rounded-full border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 transition hover:bg-zinc-50"
              >
                Reset Filters
              </button>
            </div>
          ) : (
            <div className="divide-y divide-zinc-200">
              {filteredHosts.map((host) => (
                <button
                  key={host._id}
                  type="button"
                  onClick={() => navigate(`/hostList/${host._id}`)}
                  className="group flex w-full items-start gap-4 px-5 py-4 text-left transition hover:bg-zinc-50"
                >
                  <div className={joinClasses(
                    'mt-1 flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-sm font-semibold',
                    host.active ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                  )}>
                    {getInitials(host)}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate text-base font-semibold text-zinc-900">{host.displayName}</p>
                      <StatusBadge active={host.active} />
                      <SecondaryBadge label={host.isVerified ? 'Verified' : 'Unverified'} tone={host.isVerified ? 'emerald' : 'amber'} />
                      <SecondaryBadge label={host.isOnBoarded ? 'Onboarded' : 'Needs onboarding'} tone={host.isOnBoarded ? 'sky' : 'amber'} />
                      {!host.propertyCount ? <SecondaryBadge label="No properties" tone="zinc" /> : null}
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-zinc-500">
                      {host.email ? (
                        <span className="inline-flex items-center gap-1.5">
                          <FiMail className="h-4 w-4" />
                          {host.email}
                        </span>
                      ) : null}
                      {host.phone ? (
                        <span className="inline-flex items-center gap-1.5">
                          <FiPhone className="h-4 w-4" />
                          {host.phone}
                        </span>
                      ) : null}
                      <span className="inline-flex items-center gap-1.5">
                        <FiHome className="h-4 w-4" />
                        {numberFormatter.format(host.propertyCount)} {host.propertyCount === 1 ? 'property' : 'properties'}
                      </span>
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-zinc-400">
                      <span>Company: <span className="font-medium text-zinc-600">{host.company || 'Independent host'}</span></span>
                      <span>Created: <span className="font-medium text-zinc-600">{formatDate(host.createdAt)}</span></span>
                    </div>
                  </div>

                  <div className="mt-1 flex shrink-0 items-center gap-2 text-sm font-medium text-zinc-400 transition group-hover:text-zinc-700">
                    <span>Open</span>
                    <FiChevronRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </PageLayout>
  );
}
